/**
 * calendarSpec — Google Calendar + Outlook Calendar.
 *
 * v1 source-of-truth:
 *   ando-frontend/app/pages/GoogleCalendarPage.tsx (527 LoC)
 *   ando-frontend/app/pages/OutlookCalendarPage.tsx (532 LoC)
 *
 * v1 flow: OAuth → fetch list of available calendars → user picks which
 * to sync → events stream into the workspace. The "select calendars" step
 * lives in a follow-up post-connect screen in v1 (BE-driven). For the v2
 * wizard we keep the wizard scope to the auth handshake; calendar selection
 * happens after redirect inside the Calendar tab settings drawer.
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Calendar, CalendarClock, Shield, Zap } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";

export interface CalendarConnection extends BaseConnection {
  readonly email?: string;
  readonly display_name?: string;
  readonly calendar_count?: number;
}

const iconCls = "h-4 w-4";

export const calendarSpec: CapabilityWizardSpec<CalendarConnection> = {
  capability: "calendar",
  title: msg`Calendario`,
  subtitle: msg`Eventi, brief pre-meeting, finestre di disponibilità`,
  icon: <Calendar className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 15,

  // Vendor IDs match the BE ``_LEGACY_OAUTH_START_URL`` keys so the
  // unified ``/connectors/{capability}/start`` endpoint resolves the
  // OAuth redirect URL on the first try. Using just ``google`` /
  // ``outlook`` would 404 against the per-vendor calendar OAuth route
  // (which is mounted under ``/calendar/auth/{google_calendar,
  // outlook_calendar}/connect``).
  vendors: [
    {
      id: "google_calendar",
      label: "Google Calendar",
      icon: <Calendar className={iconCls} aria-hidden />,
    },
    {
      id: "outlook_calendar",
      label: "Outlook Calendar",
      icon: <Calendar className={iconCls} aria-hidden />,
    },
  ],

  // ``vendor_picker`` retired May 24 2026 — picker is now AddAccountMenu.
  steps: [
    {
      id: "authorize",
      kind: "oauth",
      title: msg`Autorizza`,
      description: msg`Ando potrà leggere/creare eventi e suggerire slot liberi.`,
      oauth: { buttonLabel: msg`Autorizza calendario` },
    },
  ],

  benefits: [
    {
      icon: <CalendarClock className={iconCls} aria-hidden />,
      title: msg`Brief pre-meeting`,
      body: msg`Email, deal correlati, note: tutto pronto 5 minuti prima.`,
    },
    {
      icon: <Zap className={iconCls} aria-hidden />,
      title: msg`Slot proposti`,
      body: msg`"Trovami 30 minuti con Mario settimana prossima" → tre opzioni.`,
    },
    {
      icon: <Calendar className={iconCls} aria-hidden />,
      title: msg`Multi-calendario`,
      body: msg`Personale, team, condivisi — un'unica timeline.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Privato`,
      body: msg`Niente lettura di partecipanti esterni o eventi privati senza consenso.`,
    },
  ],

  api: createCapabilityApi<CalendarConnection>({ capability: "calendar" }),
  connectionKeyField: "id" as keyof CalendarConnection,
  getDisplayName: (c) =>
    c.display_name ?? c.email ?? c.id ?? i18nSingleton._(msg`Calendario`),
  getSecondaryId: (c) => c.email ?? null,
};
